import { useEffect, useState } from 'react';
import Modal from '../../../shared/components/Modal';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { getAllTeachers } from '../../Teacher/services/teacherService';
import { getDistinctSections } from '../../Student/services/studentService';

const CATEGORIES = ['Core', 'Applied', 'Specialized'];

// The DB defaults (see supabase/schema.sql); attendance stays out of the grade until given a weight.
const WEIGHT_FIELDS = [
  { key: 'ww_weight', label: 'Written Works', fallback: 25 },
  { key: 'pt_weight', label: 'Performance Task', fallback: 45 },
  { key: 'exam_weight', label: 'Examination', fallback: 30 },
  { key: 'att_weight', label: 'Attendance', fallback: 0 },
];

function SubjectFormModal({ subject, onClose, onSubmit }) {
  const [name, setName] = useState(subject?.name ?? '');
  const [category, setCategory] = useState(subject?.category ?? CATEGORIES[0]);
  const [teacherId, setTeacherId] = useState(subject?.teacher_id ?? '');
  const [gradeLevel, setGradeLevel] = useState(subject?.grade_level ?? GRADE_LEVELS[0].value);
  const [sections, setSections] = useState(() => (subject?.subject_sections ?? []).map((s) => s.section));
  const [gradeSections, setGradeSections] = useState([]);
  const [weights, setWeights] = useState(() =>
    Object.fromEntries(WEIGHT_FIELDS.map((w) => [w.key, String(Number(subject?.[w.key] ?? w.fallback))]))
  );
  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getAllTeachers()
      .then(setTeachers)
      .catch(() => setTeachers([]));
  }, []);

  useEffect(() => {
    getDistinctSections(gradeLevel)
      .then(setGradeSections)
      .catch(() => setGradeSections([]));
  }, [gradeLevel]);

  // A section already assigned stays listed even if no student is in it anymore.
  const sectionOptions = [...new Set([...gradeSections, ...sections])].sort();

  const handleGradeLevelChange = (value) => {
    setGradeLevel(value);
    setSections([]);
  };

  const toggleSection = (name) =>
    setSections((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));

  const weightTotal = WEIGHT_FIELDS.reduce((sum, w) => sum + (Number(weights[w.key]) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Rounded because 0.1 + 0.2-style sums of decimal weights aren't exact in floating point.
    if (Math.round(weightTotal * 100) !== 10000) {
      setError(`The grade weights must total 100% (currently ${Math.round(weightTotal * 100) / 100}%).`);
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        category,
        teacher_id: teacherId || null,
        grade_level: gradeLevel || null,
        sections,
        ...Object.fromEntries(WEIGHT_FIELDS.map((w) => [w.key, Number(weights[w.key]) || 0])),
      });
    } catch (err) {
      setError(err.message || 'Failed to save subject');
      setSaving(false);
    }
  };

  return (
    <Modal title={subject ? 'Edit Subject' : 'Add Subject'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <div className="space-y-1">
          <label htmlFor="name" className="text-sm font-medium text-gray-700">Subject Name</label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="category" className="text-sm font-medium text-gray-700">Category</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="teacher" className="text-sm font-medium text-gray-700">Teacher</label>
            <select
              id="teacher"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              <option value="">Unassigned</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>{t.firstname} {t.lastname}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="gradeLevel" className="text-sm font-medium text-gray-700">Grade Level</label>
          <select
            id="gradeLevel"
            value={gradeLevel}
            onChange={(e) => handleGradeLevelChange(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          >
            {GRADE_LEVELS.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">Sections</p>
            {sectionOptions.length > 0 && (
              <button
                type="button"
                onClick={() => setSections(sections.length === sectionOptions.length ? [] : sectionOptions)}
                className="text-xs font-medium text-[#0b4a71] hover:text-[#3f6d8e]"
              >
                {sections.length === sectionOptions.length ? 'Clear all' : 'Select all'}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">Only students in the selected sections are listed for this subject.</p>
          {sectionOptions.length === 0 ? (
            <p className="text-sm text-gray-500 rounded-md border border-gray-200 px-3 py-3 text-center">
              No sections found for this grade level.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 px-3 py-2">
              {sectionOptions.map((name) => (
                <label key={name} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={sections.includes(name)}
                    onChange={() => toggleSection(name)}
                    className="rounded border-gray-300 text-[#0b4a71] focus:ring-[#4ea6d0]"
                  />
                  {name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">Grade Weights (%)</p>
            <p className={`text-xs font-medium ${Math.round(weightTotal * 100) === 10000 ? 'text-green-600' : 'text-red-600'}`}>
              Total: {Math.round(weightTotal * 100) / 100}%
            </p>
          </div>
          <p className="text-xs text-gray-500">Must total 100. Leave Attendance at 0 to keep it out of the grade.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {WEIGHT_FIELDS.map((w) => (
              <div key={w.key} className="space-y-1">
                <label htmlFor={w.key} className="text-xs font-medium text-gray-600">{w.label}</label>
                <input
                  id={w.key}
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  value={weights[w.key]}
                  onChange={(e) => setWeights((prev) => ({ ...prev, [w.key]: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default SubjectFormModal;
